/**
 * AgentIntegrationAllowlist — per-agent integration allowlist (least-privilege)
 * editor for a DEPLOYED workflow.
 *
 * Lists the account's CONNECTED integrations (from
 * GET /workflows/{uuid}/integrations/status) with a checkbox each. Checked =
 * this agent may use the integration's tools. REQUIRED-connected integrations
 * are LOCKED on (the agent depends on them; the server force-includes them).
 *
 * Lifecycle / backward-compat:
 *   - The workflow row's `enabledIntegrations` (passed via `stored`) is the
 *     current allowlist. ABSENT/null = "all connected allowed" → every
 *     connected integration starts selected (default all-on, deselectable).
 *   - Save PATCHes /workflows/{uuid}. We send the explicit array when it's a
 *     strict SUBSET of all connected; when everything is selected we send
 *     `null` to CLEAR the allowlist (revert to the all-connected default) so the
 *     row never carries a redundant "all of them" list.
 *
 * Reuses the marketplace modal's `mkt-modal-integration*` row styles (imported
 * by IntegrationsConnectList's siblings) for visual consistency.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { lockClosedOutline } from 'ionicons/icons';
import api from '../../../services/api';
import IntegrationLogo from '../IntegrationLogo';
import {
  connectedProviders as computeConnectedProviders,
  requiredConnectedProviders as computeRequiredConnected,
  initialSelection as computeInitialSelection,
} from '../../../utils/enabledIntegrations';
import '../../../pages/MarketplacePage/MarketplaceDetailModal.css';

const setsEqual = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));

const AgentIntegrationAllowlist = ({ workflowUuid, stored, setToast, onSaved }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workflowUuid) { setStatus(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/workflows/${encodeURIComponent(workflowUuid)}/integrations/status`)
      .then((res) => { if (!cancelled) setStatus(res || null); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load integration status'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowUuid]);

  // Initialize the selection from the stored allowlist (or all-on default) once
  // status loads. Re-derive if the stored value or status changes.
  useEffect(() => {
    setSelected(computeInitialSelection(status, Array.isArray(stored) ? stored : null));
  }, [status, stored]);

  const connected = computeConnectedProviders(status);
  const requiredLocked = computeRequiredConnected(status);

  const toggle = (id) => {
    if (requiredLocked.has(id)) return; // locked — never toggleable
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Dirty = selection differs from the stored allowlist (normalized through the
  // same initializer so "all-on default" vs "explicit all" compare equal).
  const baseline = computeInitialSelection(status, Array.isArray(stored) ? stored : null);
  const dirty = !setsEqual(selected, baseline);

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Strict subset → send the explicit array (required members force-added
      // server-side). Everything selected → send null to clear the allowlist
      // (revert to "all connected allowed").
      const chosen = [...selected].filter((id) => connected.has(id));
      const isStrictSubset = connected.size > 0 && chosen.length < connected.size;
      const payload = { enabledIntegrations: isStrictSubset ? chosen : null };
      const res = await api.patch(`/workflows/${encodeURIComponent(workflowUuid)}`, payload);
      setToast?.({ show: true, message: 'Integration access updated', color: 'success' });
      onSaved?.(res?.enabledIntegrations ?? null);
    } catch (err) {
      setToast?.({ show: true, message: err?.message || 'Failed to update integration access', color: 'danger' });
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, selected, connected, workflowUuid]);

  if (loading) {
    return (
      <div className="int-connect-loading">
        <IonSpinner name="dots" />
        <span>Loading integrations…</span>
      </div>
    );
  }
  if (error) {
    return (
      <p className="int-connect-error">
        Couldn’t load integration status. You can manage integrations from{' '}
        <a href="/integrations">Integrations</a>.
      </p>
    );
  }
  if (connected.size === 0) {
    return <p className="int-connect-none">No connected integrations to manage for this agent.</p>;
  }

  return (
    <>
      <ul className="mkt-modal-integrations">
        {[...connected].sort().map((id) => {
          const meta = status?.integrations?.[id] || { id, name: id };
          const locked = requiredLocked.has(id);
          const checked = locked || selected.has(id);
          return (
            <li key={id} className="mkt-modal-integration-row is-connected">
              <span className="mkt-modal-integration-brand">
                <IntegrationLogo id={id} size={18} title={meta.name} />
                <span className="mkt-modal-integration-name">{meta.name}</span>
              </span>
              <span className="mkt-modal-integration-state">
                <label
                  className={`mkt-modal-integration-allow${locked ? ' is-locked' : ''}`}
                  title={locked ? 'Required by this agent — always enabled' : 'Allow this agent to use this integration'}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={locked}
                    onChange={() => toggle(id)}
                    aria-label={locked ? `${meta.name} (required — always enabled)` : `Enable ${meta.name} for this agent`}
                  />
                  <span className="mkt-modal-integration-allow-label">
                    {locked ? (<><IonIcon icon={lockClosedOutline} aria-hidden="true" /> Required</>) : 'Enabled'}
                  </span>
                </label>
              </span>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="pipeline-update-btn pipeline-update-btn--primary"
        style={{ marginTop: 12 }}
        disabled={!dirty || saving}
        onClick={handleSave}
      >
        {saving ? 'Saving…' : 'Save access'}
      </button>
    </>
  );
};

export default AgentIntegrationAllowlist;
